/**
 * demoData.ts — Centralised Demo Data (Single Source of Truth)
 *
 * ALL seeded data lives here, organised by domain.
 * Screens NEVER contain demo data — if you need to adjust, come here.
 * Totals are derived from entries, never hardcoded.
 * All exports are strictly typed against production types.
 *
 * Place at: src/demo/demoData.ts
 */

import type { NutritionEntry, DailyNutrition, TargetRanges } from '../types/nutrition';
import type { WorkoutEntry } from '../types/workout';
import type { BodyStatsEntry } from '../types/bodyStats';
import type { DailyTDEE, BodyMetrics } from '../types/tdee';
import type { FunctionalAgeSnapshot } from '../types/functionalAge';
import { demoToday, demoYesterday } from './demoClock';
import { demoPersona } from './demoPersona';

const DEMO_USER_ID = demoPersona.userId;

/** Format a Date as YYYY-MM-DD using LOCAL timezone (never UTC). */
function localDateStr(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** Get a date string for N days before demo today. */
function daysAgoStr(n: number): string {
  const d = new Date(demoToday());
  d.setDate(d.getDate() - n);
  return localDateStr(d);
}

// ── Profile ──────────────────────────────────────────────────────

export const demoProfile = {
  first_name: demoPersona.firstName,
  units: demoPersona.units,
  weight_kg: demoPersona.currentWeightKg,
  body_fat_percent: demoPersona.bodyFatPercent,
};

// ── Targets ──────────────────────────────────────────────────────
// Hydration target 2500ml (EFSA male guidance) — Alex logs ~1600ml → amber preserved
// Fibre target 35g so Alex falls short → amber

export const demoTargets: TargetRanges = {
  userId: DEMO_USER_ID,
  calories: { target: 1950, min: 1700, max: 2200 },
  protein: { target: 162, min: 108, max: 180 }, // 1.2–2.0 g/kg × 90kg; target 1.8 g/kg (GLP-1)
  carbs: { target: 180, min: 100, max: 220 }, // rest day min 100g; training day min 130g applied dynamically in checkNutritionRanges
  fat: { min: 33 }, // EFSA AMDR lower bound: 20% of 1500 kcal floor ÷ 9 = 33g
  fibre: { min: 25 }, // EFSA adequate intake — Alex logs ~16g so still falls short → amber preserved
  hydration: { min: 2500 }, // EFSA male guidance — Alex logs ~1600ml → amber preserved
  creatine: { min: 3, max: 5 },
  dailyDeficit: { target: 500, min: 300, max: 700 },
  weeklyDeficit: { target: 3500, min: 2100, max: 4900 },
  createdAt: Date.now(),
  updatedAt: Date.now(),
};

// ── Body Metrics ─────────────────────────────────────────────────

export const demoBodyMetrics: BodyMetrics = {
  userId: DEMO_USER_ID,
  currentWeight: demoPersona.currentWeightKg,
  height: demoPersona.heightCm,
  age: demoPersona.age,
  sex: demoPersona.sex,
  currentBodyFat: demoPersona.bodyFatPercent,
  muscleMass: demoPersona.muscleMassKg,
  goalWeight: demoPersona.goalWeightKg,
  goalBodyFat: demoPersona.goalBodyFat,
  goalType: 'lose',
  restingHR: 68,
  bloodPressure: { systolic: 122, diastolic: 78 },
  waistCm: 86, // current waist — WHtR = 86/179 = 0.48 (healthy range)
  createdAt: Date.now(),
  updatedAt: Date.now(),
};

// ══════════════════════════════════════════════════════════════════
// ── NUTRITION HISTORY (14 days) ──────────────────────────────────
// ══════════════════════════════════════════════════════════════════
// Each day has 3–4 food entries with realistic variation.
// Fibre stays low most days (GLP-1 users struggle with fibre).
// Hydration stays below target most days → amber driver.
// Some days are better/worse than others for comparison realism.

type NutritionSource = NutritionEntry['source'];

interface DayMealPlan {
  meals: Array<{
    time: string;
    desc: string;
    cal: number;
    pro: number;
    carbs: number;
    fat: number;
    fibre: number;
    hydMl?: number;
    source?: NutritionSource;
    creatine?: number;
    collagen?: number;
  }>;
  waterMl: number[]; // separate hydration entries
}

/**
 * 14 days of meal plans. Index 0 = today, index 1 = yesterday, etc.
 * Varied but realistic for a GLP-1 user trying to eat clean.
 */
const MEAL_HISTORY: DayMealPlan[] = [
  // Day 0 — TODAY (1485cal, 153p, 135c, 31f, 16fi)
  {
    meals: [
      { time: '08:15', desc: 'Skyr yoghurt with blueberries', cal: 195, pro: 30, carbs: 14, fat: 2, fibre: 2, source: 'manual', creatine: 5, collagen: 10 },
      { time: '12:45', desc: 'Chicken breast, brown rice, roasted vegetables', cal: 520, pro: 48, carbs: 55, fat: 8, fibre: 7, source: 'text-ai-estimate' },
      { time: '15:30', desc: 'Protein shake with banana', cal: 210, pro: 35, carbs: 24, fat: 3, fibre: 1, hydMl: 350, source: 'manual' },
      { time: '18:30', desc: 'Salmon fillet, sweet potato, mixed greens', cal: 560, pro: 40, carbs: 42, fat: 18, fibre: 6, source: 'text-ai-estimate' },
    ],
    waterMl: [300, 350, 400, 200],
  },
  // Day 1 — YESTERDAY (solid day, slightly higher cals)
  {
    meals: [
      { time: '07:45', desc: 'Porridge with protein powder and berries', cal: 310, pro: 28, carbs: 40, fat: 5, fibre: 4, source: 'manual' },
      { time: '12:30', desc: 'Turkey wrap with avocado and spinach', cal: 480, pro: 38, carbs: 35, fat: 16, fibre: 5, source: 'text-ai-estimate' },
      { time: '16:00', desc: 'Greek yoghurt with honey and almonds', cal: 220, pro: 18, carbs: 20, fat: 8, fibre: 1, source: 'manual' },
      { time: '19:00', desc: 'Lean beef stir fry with noodles', cal: 580, pro: 42, carbs: 52, fat: 14, fibre: 5, source: 'text-ai-estimate' },
    ],
    waterMl: [350, 350, 400, 300],
  },
  // Day 2 — Low appetite day (GLP-1 effect, lower cals)
  {
    meals: [
      { time: '09:00', desc: 'Scrambled eggs on toast', cal: 320, pro: 22, carbs: 28, fat: 14, fibre: 2, source: 'manual' },
      { time: '13:30', desc: 'Tuna salad with mixed leaves', cal: 340, pro: 35, carbs: 12, fat: 16, fibre: 3, source: 'text-ai-estimate' },
      { time: '18:00', desc: 'Chicken soup with crusty bread', cal: 380, pro: 28, carbs: 35, fat: 10, fibre: 3, source: 'text-ai-estimate' },
    ],
    waterMl: [300, 350, 350, 200],
  },
  // Day 3 — Higher protein day
  {
    meals: [
      { time: '07:30', desc: 'Protein pancakes with maple syrup', cal: 350, pro: 32, carbs: 38, fat: 6, fibre: 2, source: 'manual' },
      { time: '12:00', desc: 'Grilled chicken salad with feta and quinoa', cal: 520, pro: 45, carbs: 35, fat: 18, fibre: 5, source: 'text-ai-estimate' },
      { time: '15:00', desc: 'Protein bar', cal: 220, pro: 20, carbs: 22, fat: 8, fibre: 3, source: 'manual' },
      { time: '19:00', desc: 'Cod fillet with roasted vegetables', cal: 420, pro: 38, carbs: 30, fat: 12, fibre: 6, source: 'text-ai-estimate' },
    ],
    waterMl: [350, 300, 400, 200],
  },
  // Day 4 — Slightly over target (treat day)
  {
    meals: [
      { time: '08:00', desc: 'Overnight oats with banana and peanut butter', cal: 420, pro: 18, carbs: 55, fat: 16, fibre: 5, source: 'manual' },
      { time: '12:30', desc: 'Chicken Caesar wrap', cal: 520, pro: 35, carbs: 40, fat: 20, fibre: 3, source: 'text-ai-estimate' },
      { time: '16:00', desc: 'Dark chocolate and almonds', cal: 250, pro: 6, carbs: 22, fat: 16, fibre: 3, source: 'manual' },
      { time: '19:30', desc: 'Homemade chicken burger with sweet potato fries', cal: 680, pro: 40, carbs: 60, fat: 24, fibre: 5, source: 'text-ai-estimate' },
    ],
    waterMl: [300, 300, 350, 250],
  },
  // Day 5 — Good balanced day
  {
    meals: [
      { time: '07:45', desc: 'Smoked salmon and cream cheese bagel', cal: 380, pro: 24, carbs: 35, fat: 14, fibre: 2, source: 'manual' },
      { time: '12:15', desc: 'Lentil and vegetable soup with bread', cal: 410, pro: 22, carbs: 52, fat: 8, fibre: 9, source: 'text-ai-estimate' },
      { time: '15:30', desc: 'Protein shake', cal: 180, pro: 30, carbs: 8, fat: 3, fibre: 0, hydMl: 350, source: 'manual' },
      { time: '18:30', desc: 'Prawn stir fry with brown rice', cal: 490, pro: 34, carbs: 48, fat: 12, fibre: 5, source: 'text-ai-estimate' },
    ],
    waterMl: [350, 350, 400, 300],
  },
  // Day 6 — Moderate day
  {
    meals: [
      { time: '08:30', desc: 'Two boiled eggs with avocado toast', cal: 380, pro: 20, carbs: 28, fat: 22, fibre: 4, source: 'manual' },
      { time: '13:00', desc: 'Chicken and avocado protein bagel', cal: 410, pro: 38, carbs: 35, fat: 12, fibre: 4, source: 'manual' },
      { time: '18:00', desc: 'Baked sweet potato with chilli con carne', cal: 540, pro: 32, carbs: 55, fat: 14, fibre: 8, source: 'text-ai-estimate' },
    ],
    waterMl: [300, 350, 400, 200],
  },
  // Day 7 — Sunday rest day (lighter eating)
  {
    meals: [
      { time: '09:30', desc: 'Granola with yoghurt and mixed berries', cal: 350, pro: 16, carbs: 48, fat: 10, fibre: 4, source: 'manual' },
      { time: '13:00', desc: 'Roast chicken with vegetables and gravy', cal: 580, pro: 42, carbs: 38, fat: 22, fibre: 6, source: 'text-ai-estimate' },
      { time: '18:30', desc: 'Cheese omelette with side salad', cal: 340, pro: 24, carbs: 8, fat: 24, fibre: 2, source: 'manual' },
    ],
    waterMl: [250, 350, 300, 200],
  },
  // Day 8 — Solid gym day
  {
    meals: [
      { time: '07:00', desc: 'Skyr yoghurt with granola', cal: 280, pro: 26, carbs: 30, fat: 5, fibre: 2, source: 'manual' },
      { time: '12:00', desc: 'Tuna and sweetcorn jacket potato', cal: 480, pro: 35, carbs: 52, fat: 10, fibre: 4, source: 'text-ai-estimate' },
      { time: '15:30', desc: 'Grenade protein bar', cal: 220, pro: 20, carbs: 17, fat: 8, fibre: 2, source: 'manual' },
      { time: '19:00', desc: 'Chicken tikka with rice and naan', cal: 620, pro: 38, carbs: 65, fat: 16, fibre: 4, source: 'text-ai-estimate' },
    ],
    waterMl: [350, 400, 400, 250],
  },
  // Day 9 — Low appetite again
  {
    meals: [
      { time: '08:30', desc: 'Toast with peanut butter', cal: 260, pro: 10, carbs: 28, fat: 12, fibre: 3, source: 'manual' },
      { time: '13:00', desc: 'Chicken wrap with hummus', cal: 420, pro: 32, carbs: 38, fat: 12, fibre: 4, source: 'text-ai-estimate' },
      { time: '18:30', desc: 'Vegetable curry with rice', cal: 460, pro: 14, carbs: 62, fat: 16, fibre: 7, source: 'text-ai-estimate' },
    ],
    waterMl: [300, 300, 350, 200],
  },
  // Day 10 — Higher calorie day
  {
    meals: [
      { time: '07:30', desc: 'Full English breakfast (reduced)', cal: 480, pro: 28, carbs: 35, fat: 24, fibre: 3, source: 'manual' },
      { time: '12:30', desc: 'BLT sandwich with crisps', cal: 520, pro: 20, carbs: 48, fat: 28, fibre: 3, source: 'text-ai-estimate' },
      { time: '16:00', desc: 'Protein shake with banana', cal: 210, pro: 35, carbs: 24, fat: 3, fibre: 1, hydMl: 350, source: 'manual' },
      { time: '19:00', desc: 'Spaghetti bolognese', cal: 580, pro: 32, carbs: 60, fat: 18, fibre: 5, source: 'text-ai-estimate' },
    ],
    waterMl: [300, 350, 400, 250],
  },
  // Day 11 — Good protein day
  {
    meals: [
      { time: '07:45', desc: 'Scrambled eggs with smoked salmon', cal: 340, pro: 30, carbs: 4, fat: 22, fibre: 0, source: 'manual' },
      { time: '12:00', desc: 'Chicken and bean burrito bowl', cal: 520, pro: 40, carbs: 48, fat: 14, fibre: 8, source: 'text-ai-estimate' },
      { time: '15:30', desc: 'Cottage cheese with pineapple', cal: 160, pro: 20, carbs: 14, fat: 2, fibre: 1, source: 'manual' },
      { time: '18:30', desc: 'Grilled steak with roasted broccoli and rice', cal: 560, pro: 44, carbs: 38, fat: 20, fibre: 5, source: 'text-ai-estimate' },
    ],
    waterMl: [350, 300, 400, 250],
  },
  // Day 12 — Moderate
  {
    meals: [
      { time: '08:00', desc: 'Porridge with blueberries', cal: 250, pro: 10, carbs: 42, fat: 4, fibre: 4, source: 'manual' },
      { time: '12:30', desc: 'Ham and cheese toastie with soup', cal: 440, pro: 24, carbs: 38, fat: 20, fibre: 3, source: 'text-ai-estimate' },
      { time: '18:00', desc: 'Baked salmon with new potatoes and green beans', cal: 520, pro: 38, carbs: 35, fat: 22, fibre: 5, source: 'text-ai-estimate' },
    ],
    waterMl: [300, 300, 350, 200],
  },
  // Day 13 — Two weeks ago
  {
    meals: [
      { time: '07:30', desc: 'Banana and protein smoothie', cal: 280, pro: 28, carbs: 32, fat: 4, fibre: 2, source: 'manual' },
      { time: '12:00', desc: 'Grilled halloumi wrap with peppers', cal: 460, pro: 24, carbs: 40, fat: 20, fibre: 4, source: 'text-ai-estimate' },
      { time: '15:00', desc: 'Apple with peanut butter', cal: 200, pro: 6, carbs: 22, fat: 10, fibre: 3, source: 'manual' },
      { time: '19:00', desc: 'Lamb mince kofta with flatbread and salad', cal: 540, pro: 34, carbs: 42, fat: 22, fibre: 4, source: 'text-ai-estimate' },
    ],
    waterMl: [300, 350, 350, 200],
  },
];

// ── Nutrition Entries (ALL days — food only, water goes in hydrationEntries) ──

export function getDemoNutritionEntries(): NutritionEntry[] {
  const entries: NutritionEntry[] = [];

  for (let dayIndex = 0; dayIndex < MEAL_HISTORY.length; dayIndex++) {
    const dateStr = daysAgoStr(dayIndex);
    const day = MEAL_HISTORY[dayIndex];

    day.meals.forEach((meal, mealIndex) => {
      entries.push({
        id: `demo-nutrition-d${dayIndex}-m${mealIndex}`,
        userId: DEMO_USER_ID,
        date: dateStr,
        timestamp: new Date(`${dateStr}T${meal.time}:00`).getTime(),
        description: meal.desc,
        amount: 1,
        unit: 'g',
        macros: {
          calories: meal.cal,
          protein: meal.pro,
          carbs: meal.carbs,
          fat: meal.fat,
          fibre: meal.fibre,
        },
        hydrationMl: meal.hydMl ?? 0,
        source: meal.source ?? 'manual',
        ...(meal.creatine ? { creatine: meal.creatine } : {}),
        ...(meal.collagen ? { collagen: meal.collagen } : {}),
      });
    });
  }

  return entries;
}

// ── Hydration Entries (ALL days — separate collection, water/tea only) ────

export function getDemoHydrationEntries(): Record<string, any>[] {
  const entries: Record<string, any>[] = [];
  const waterTimes = ['07:30', '10:00', '14:30', '19:30'];
  const waterDescs = ['Water', 'Water', 'Water', 'Herbal tea'];

  for (let dayIndex = 0; dayIndex < MEAL_HISTORY.length; dayIndex++) {
    const dateStr = daysAgoStr(dayIndex);
    const day = MEAL_HISTORY[dayIndex];

    day.waterMl.forEach((ml, idx) => {
      entries.push({
        id: `demo-hydration-d${dayIndex}-${idx}`,
        userId: DEMO_USER_ID,
        date: dateStr,
        timestamp: new Date(`${dateStr}T${waterTimes[idx] ?? '12:00'}:00`).getTime(),
        description: waterDescs[idx] ?? 'Water',
        amount: ml,
      });
    });
  }

  return entries;
}

// ── Daily Nutrition (derived from entries — never hardcoded) ─────
// Returns a single day's summary. Used for today's snapshot.

export function getDemoDailyNutrition(): DailyNutrition {
  return getDemoDailyNutritionForDate(demoToday());
}

/**
 * Returns a daily nutrition summary for any given date string.
 * Derives totals from entries — never hardcoded.
 */
export function getDemoDailyNutritionForDate(dateStr: string): DailyNutrition {
  const allNutrition = getDemoNutritionEntries();
  const allHydration = getDemoHydrationEntries();

  const dayFood = allNutrition.filter((e) => e.date === dateStr);
  const dayWater = allHydration.filter((e) => e.date === dateStr);

  const totalCalories = dayFood.reduce((s, e) => s + e.macros.calories, 0);
  const totalProtein = dayFood.reduce((s, e) => s + e.macros.protein, 0);
  const totalCarbs = dayFood.reduce((s, e) => s + e.macros.carbs, 0);
  const totalFat = dayFood.reduce((s, e) => s + e.macros.fat, 0);
  const totalFibre = dayFood.reduce((s, e) => s + e.macros.fibre, 0);
  const totalFoodHydration = dayFood.reduce((s, e) => s + (e.hydrationMl ?? 0), 0);
  const totalHydration = totalFoodHydration + dayWater.reduce((s, e) => s + (e.amount ?? 0), 0);

  const supplements = getDemoSupplements();
  const totalCreatine = supplements.find((s) => s.type === 'creatine')?.grams ?? 0;
  const totalCollagen = supplements.find((s) => s.type === 'collagen')?.grams ?? 0;

  return {
    date: dateStr,
    userId: DEMO_USER_ID,
    totalCalories,
    totalProtein,
    totalCarbs,
    totalFat,
    totalFibre,
    totalCreatine,
    totalCollagen,
    totalFoodHydration,
    totalHydration,
    entries: dayFood,
    hydrationEntries: [],
  };
}

/**
 * Returns daily nutrition summaries for ALL seeded days.
 * Used by the router for comparison views and dailyNutrition collection queries.
 */
export function getDemoAllDailyNutrition(): DailyNutrition[] {
  const summaries: DailyNutrition[] = [];
  for (let dayIndex = 0; dayIndex < MEAL_HISTORY.length; dayIndex++) {
    const dateStr = daysAgoStr(dayIndex);
    summaries.push(getDemoDailyNutritionForDate(dateStr));
  }
  return summaries;
}

// ── Supplements ──────────────────────────────────────────────────

export interface DemoSupplementEntry {
  type: string;
  label: string;
  grams: number;
}

export function getDemoSupplements(): DemoSupplementEntry[] {
  return [
    { type: 'creatine', label: 'Creatine', grams: 5 },
    { type: 'collagen', label: 'Collagen', grams: 10 },
  ];
}

/**
 * Returns supplement entries as a Firestore-style collection.
 * Each supplement is its own document so SupplementsRow can query them
 * regardless of whether it reads from a dedicated collection or dailyNutrition fields.
 */
export function getDemoSupplementEntries(): Record<string, any>[] {
  const today = demoToday();
  return [
    {
      id: 'demo-supplement-1',
      userId: DEMO_USER_ID,
      date: today,
      type: 'creatine',
      label: 'Creatine',
      grams: 5,
      timestamp: new Date(`${today}T07:00:00`).getTime(),
    },
    {
      id: 'demo-supplement-2',
      userId: DEMO_USER_ID,
      date: today,
      type: 'collagen',
      label: 'Collagen',
      grams: 10,
      timestamp: new Date(`${today}T07:00:00`).getTime(),
    },
  ];
}

// ── Workouts (today) ─────────────────────────────────────────────

export function getDemoWorkoutEntries(): WorkoutEntry[] {
  const today = demoToday();
  return [
    {
      id: 'demo-workout-1',
      userId: DEMO_USER_ID,
      date: today,
      timestamp: new Date(`${today}T06:45:00`).getTime(),
      type: 'weightlifting',
      description: 'Upper body Push — bench, OHP, lateral raises, tricep dips',
      caloriesBurned: 420,
      duration: 55,
      categories: ['Push'],
      createdAt: new Date(`${today}T06:45:00`).getTime(),
      updatedAt: new Date(`${today}T06:45:00`).getTime(),
    },
    {
      id: 'demo-workout-2',
      userId: DEMO_USER_ID,
      date: today,
      timestamp: new Date(`${today}T17:00:00`).getTime(),
      type: 'walk',
      description: 'Evening walk',
      caloriesBurned: 180,
      duration: 35,
      distance: 3.2,
      steps: 4200,
      createdAt: new Date(`${today}T17:00:00`).getTime(),
      updatedAt: new Date(`${today}T17:00:00`).getTime(),
    },
  ];
}

// ── Workouts (yesterday — for EPOC) ──────────────────────────────

export function getDemoYesterdayWorkoutEntries(): WorkoutEntry[] {
  const yesterday = demoYesterday();
  return [
    {
      id: 'demo-workout-y1',
      userId: DEMO_USER_ID,
      date: yesterday,
      timestamp: new Date(`${yesterday}T06:30:00`).getTime(),
      type: 'weightlifting',
      description: 'Upper body Pull — rows, pull-ups, face pulls, bicep curls',
      caloriesBurned: 380,
      duration: 50,
      categories: ['Pull'],
      createdAt: new Date(`${yesterday}T06:30:00`).getTime(),
      updatedAt: new Date(`${yesterday}T06:30:00`).getTime(),
    },
    {
      id: 'demo-workout-y2',
      userId: DEMO_USER_ID,
      date: yesterday,
      timestamp: new Date(`${yesterday}T12:30:00`).getTime(),
      type: 'walk',
      description: 'Lunchtime walk',
      caloriesBurned: 150,
      duration: 30,
      distance: 2.8,
      steps: 3800,
      createdAt: new Date(`${yesterday}T12:30:00`).getTime(),
      updatedAt: new Date(`${yesterday}T12:30:00`).getTime(),
    },
  ];
}

// ── All Workouts (14 days — for consistency, monthly, EPOC) ──────

export function getDemoAllWorkouts(): WorkoutEntry[] {
  const today = demoToday();
  const entries: WorkoutEntry[] = [];

  entries.push(...getDemoWorkoutEntries());
  entries.push(...getDemoYesterdayWorkoutEntries());

  const gymSchedule: Record<number, { desc: string; cat: string; dur: number; cal: number; type?: string }> = {
    2: { desc: 'Legs — squats, RDLs, leg press, calf raises', cat: 'Legs', dur: 50, cal: 390 },
    3: { desc: '5km easy run', cat: 'Run', dur: 28, cal: 320, type: 'run' },
    4: { desc: 'Upper body Push — bench, OHP, lateral raises', cat: 'Push', dur: 55, cal: 420 },
    5: { desc: 'Upper body Pull — rows, pull-ups, face pulls', cat: 'Pull', dur: 50, cal: 380 },
    6: { desc: 'Legs — front squats, lunges, leg curl', cat: 'Legs', dur: 45, cal: 360 },
    7: { desc: '4km recovery run', cat: 'Run', dur: 24, cal: 260, type: 'run' },
    8: { desc: 'Upper body Push — incline bench, Arnold press', cat: 'Push', dur: 55, cal: 410 },
    9: { desc: 'Upper body Pull — deadlift, barbell rows, curls', cat: 'Pull', dur: 50, cal: 400 },
    10: { desc: '6km tempo run', cat: 'Run', dur: 32, cal: 380, type: 'run' },
    11: { desc: 'Legs — squats, hip thrusts, leg extension', cat: 'Legs', dur: 50, cal: 380 },
    12: { desc: 'Upper body Push — DB bench, lateral raises, dips', cat: 'Push', dur: 50, cal: 400 },
    13: { desc: 'Upper body Pull — chin-ups, cable rows, face pulls', cat: 'Pull', dur: 45, cal: 370 },
  };

  for (let daysAgo = 2; daysAgo <= 13; daysAgo++) {
    const d = new Date(today);
    d.setDate(d.getDate() - daysAgo);
    const dateStr = localDateStr(d);
    const sched = gymSchedule[daysAgo];

    if (sched && sched.cal > 0) {
      const isRun = sched.type === 'run';
      entries.push({
        id: `demo-workout-h${daysAgo}-gym`,
        userId: demoPersona.userId,
        date: dateStr,
        timestamp: new Date(`${dateStr}T06:45:00`).getTime(),
        type: isRun ? 'run' : 'weightlifting',
        description: sched.desc,
        caloriesBurned: sched.cal,
        duration: sched.dur,
        ...(isRun ? { distance: +(sched.dur * 0.18).toFixed(1), steps: sched.dur * 170 } : {}),
        categories: [sched.cat as 'Push' | 'Pull' | 'Legs' | 'Core' | 'Run'],
        createdAt: new Date(`${dateStr}T06:45:00`).getTime(),
        updatedAt: new Date(`${dateStr}T06:45:00`).getTime(),
      });
    }

    // Walk every day
    entries.push({
      id: `demo-workout-h${daysAgo}-walk`,
      userId: demoPersona.userId,
      date: dateStr,
      timestamp: new Date(`${dateStr}T17:00:00`).getTime(),
      type: 'walk',
      description: 'Evening walk',
      caloriesBurned: 140 + Math.round(Math.sin(daysAgo) * 40),
      duration: 25 + Math.round(Math.sin(daysAgo) * 10),
      distance: +(2.5 + Math.sin(daysAgo) * 0.7).toFixed(1),
      steps: 3200 + Math.round(Math.sin(daysAgo) * 800),
      createdAt: new Date(`${dateStr}T17:00:00`).getTime(),
      updatedAt: new Date(`${dateStr}T17:00:00`).getTime(),
    });

    // Steps entry every day
    entries.push({
      id: `demo-workout-h${daysAgo}-steps`,
      userId: demoPersona.userId,
      date: dateStr,
      timestamp: new Date(`${dateStr}T20:00:00`).getTime(),
      type: 'steps',
      description: 'Daily steps',
      caloriesBurned: 0,
      duration: 0,
      steps: 6000 + Math.round(Math.sin(daysAgo * 1.5) * 2000),
      createdAt: new Date(`${dateStr}T20:00:00`).getTime(),
      updatedAt: new Date(`${dateStr}T20:00:00`).getTime(),
    });
  }

  return entries;
}

// ── Body Stats (with BP, RHR, muscle mass, 14-day history) ───────

export function getDemoBodyStatsEntries(): BodyStatsEntry[] {
  const today = demoToday();

  const entries: BodyStatsEntry[] = [];
  const startWeight = 92.8;
  const endWeight = demoPersona.currentWeightKg; // 90.0
  const startBf = 24.8;
  const endBf = demoPersona.bodyFatPercent; // 23.5
  const startMm = 61.5;
  const endMm = demoPersona.muscleMassKg; // 62.0

  for (let i = 13; i >= 0; i--) {
    const d = new Date(today);
    d.setDate(d.getDate() - i);
    const dateStr = localDateStr(d);
    const progress = (13 - i) / 13;

    const noise = Math.sin(i * 2.7) * 0.3;
    const weight = +(startWeight + (endWeight - startWeight) * progress + noise).toFixed(1);
    const bf = +(startBf + (endBf - startBf) * progress + noise * 0.1).toFixed(1);
    const mm = +(startMm + (endMm - startMm) * progress - noise * 0.05).toFixed(1);

    const entry: BodyStatsEntry = {
      id: `demo-bodystats-${i}`,
      userId: DEMO_USER_ID,
      date: dateStr,
      timestamp: new Date(`${dateStr}T07:00:00`).getTime(),
      source: 'manual',
      entry_type: 'body_stats',
      weight,
      bodyFat: bf,
      muscleMass: mm,
      createdAt: new Date(`${dateStr}T07:00:00`).getTime(),
      updatedAt: new Date(`${dateStr}T07:00:00`).getTime(),
    };

    if (i === 0) {
      entry.restingHR = 68;
      entry.bloodPressure = { systolic: 122, diastolic: 78 };
      entry.waistCm = 86; // WHtR 86/179 = 0.48 — healthy range
    }
    if (i === 7) {
      entry.waistCm = 90; // WHtR 90/179 = 0.50 — boundary
    }
    if (i === 13) {
      entry.waistCm = 95; // WHtR 95/179 = 0.53 — elevated
    }

    entries.push(entry);
  }

  return entries;
}

// ── TDEE (realistic math) ────────────────────────────────────────

export function getDemoDailyTDEE(): DailyTDEE {
  const today = demoToday();
  const baseTDEE = 2050;
  const workouts = getDemoWorkoutEntries();
  const exerciseCalories = workouts.reduce((s, w) => s + w.caloriesBurned, 0);

  const yesterdayWorkouts = getDemoYesterdayWorkoutEntries();
  const yesterdayExercise = yesterdayWorkouts.reduce((s, w) => s + w.caloriesBurned, 0);
  const epoc = Math.round(yesterdayExercise * 0.1);

  const totalTDEE = baseTDEE + exerciseCalories + epoc;
  const { totalCalories: caloriesEaten } = getDemoDailyNutrition();

  return {
    date: today,
    userId: DEMO_USER_ID,
    baseTdee: baseTDEE,
    exerciseCalories,
    epoc,
    totalTdee: totalTDEE,
    caloriesEaten,
    deficit: totalTDEE - caloriesEaten,
    isConfigured: true,
  };
}

// ── Functional Age ───────────────────────────────────────────────

export function getDemoFunctionalAge(): FunctionalAgeSnapshot {
  const today = demoToday();

  const d = new Date(today);
  const dayOfWeek = d.getDay();
  if (dayOfWeek !== 0) {
    d.setDate(d.getDate() - dayOfWeek);
  }
  const periodEnd = localDateStr(d);
  const startD = new Date(d);
  startD.setDate(startD.getDate() - 6);
  const periodStart = localDateStr(startD);

  return {
    userId: DEMO_USER_ID,
    createdAtMillis: Date.now(),
    periodStart,
    periodEnd,

    chronologicalAgeYears: demoPersona.chronologicalAge,
    overallAgeYears: demoPersona.functionalAge,
    overallScore: 72,
    deltaOverallYears: -1.2,

    calibrationPhase: false,
    algoVersion: 'v2.1',

    weights: {
      strength: 0.25,
      durability: 0.2,
      cardiac: 0.2,
      endurance: 0.15,
      bodyComp: 0.2,
    },

    components: {
      strength: {
        score: 78,
        ageYears: 29.8,
        confidence: 0.85,
        inputsSummary: { weeklyVolume: '12 sets push, 10 sets pull' },
      },
      durability: {
        score: 68,
        ageYears: 33.4,
        confidence: 0.7,
        inputsSummary: { mobilityMinutes: 15, injuryFree: true },
      },
      cardiac: {
        score: 65,
        ageYears: 33.6,
        confidence: 0.75,
        inputsSummary: { restingHR: 68, bp: '122/78' },
      },
      endurance: {
        score: 62,
        ageYears: 35.2,
        confidence: 0.6,
        inputsSummary: { weeklyCardioMinutes: 90 },
      },
      bodyComp: {
        score: 70,
        ageYears: 31.8,
        confidence: 0.9,
        inputsSummary: {
          bodyFat: demoPersona.bodyFatPercent,
          muscleMass: demoPersona.muscleMassKg,
        },
      },
    },
  };
}

export function getDemoFunctionalAgeHistory(): FunctionalAgeSnapshot[] {
  const latest = getDemoFunctionalAge();
  const history: FunctionalAgeSnapshot[] = [latest];

  const componentDeltas: Record<string, number> = {
    strength: 0.6,
    durability: 0.0,
    cardiac: 0.3,
    endurance: -0.2,
    bodyComp: 0.5,
  };

  for (let i = 1; i <= 3; i++) {
    const d = new Date(latest.periodEnd);
    d.setDate(d.getDate() - 7 * i);
    const pe = localDateStr(d);
    const sd = new Date(d);
    sd.setDate(sd.getDate() - 6);

    const components = {} as typeof latest.components;
    for (const [key, comp] of Object.entries(latest.components)) {
      const delta = componentDeltas[key] ?? 0;
      components[key as keyof typeof components] = {
        ...comp,
        ageYears: comp.ageYears !== null ? +(comp.ageYears + delta * i).toFixed(1) : null,
        score: comp.score !== null ? Math.max(0, Math.min(100, comp.score - Math.round(delta * i * 2))) : null,
      };
    }

    history.push({
      ...latest,
      periodStart: localDateStr(sd),
      periodEnd: pe,
      createdAtMillis: Date.now() - i * 7 * 86400000,
      overallAgeYears: +(demoPersona.functionalAge + i * 0.8).toFixed(1),
      overallScore: 72 - i * 3,
      deltaOverallYears: i === 3 ? null : -(0.8 + (3 - i) * 0.2),
      components,
    });
  }

  return history;
}

// ── GLP-1 Hub ────────────────────────────────────────────────────

export interface DemoGLP1StatusCard {
  daily: {
    status: 'on_track' | 'at_risk' | 'off_track';
    missCount: number;
    driverStates: Record<string, 'green' | 'amber' | 'red'>;
  };
  weekly: {
    status: 'on_track' | 'at_risk' | 'off_track';
    driverStates: Record<string, 'green' | 'amber' | 'red'>;
  };
}

export function getDemoGLP1StatusCard(): DemoGLP1StatusCard {
  return {
    daily: {
      status: 'on_track',
      missCount: 0,
      driverStates: {
        calories: 'green',
        protein: 'green',
        hydration: 'amber',
        fibre: 'amber',
        meals: 'green',
      },
    },
    weekly: {
      status: 'on_track',
      driverStates: {
        calories: 'green',
        protein: 'green',
        hydration: 'amber',
        fibre: 'amber',
        meals: 'green',
      },
    },
  };
}

export function getDemoDoseDay(): string {
  return demoPersona.doseDay;
}

// ── TDEE Config (required by calculateDailyTDEE) ────────────────

export const demoTdeeConfig = {
  userId: DEMO_USER_ID,
  baseTdee: 2050,
  effectiveBaseTdee: 2050,
  exerciseCalories: {},
  calculatedAt: Date.now() - 30 * 86400000,
  updatedAt: Date.now(),
};

// ── Deficit Plan ─────────────────────────────────────────────────

export const demoDeficitPlan = {
  userId: DEMO_USER_ID,
  currentWeight: demoPersona.currentWeightKg,
  goalWeight: demoPersona.goalWeightKg,
  timeframeWeeks: 16,
  totalDeficitNeeded: 61600,
  dailyDeficitSuggested: { min: 300, max: 700 },
  weeklyDeficitSuggested: { min: 2100, max: 4900 },
  dailyDeficitActive: { min: 300, max: 700 },
  weeklyDeficitActive: { min: 2100, max: 4900 },
  createdAt: Date.now() - 30 * 86400000,
  updatedAt: Date.now(),
};

// ── Nutrition Recents & Favourites ───────────────────────────────

export const demoNutritionRecentsFavourites = {
  userId: DEMO_USER_ID,
  recents: [
    { description: 'Skyr yoghurt with blueberries', macros: { calories: 195, protein: 30, carbs: 14, fat: 2, fibre: 2 } },
    { description: 'Chicken breast, brown rice, roasted vegetables', macros: { calories: 520, protein: 48, carbs: 55, fat: 8, fibre: 7 } },
    { description: 'Protein shake with banana', macros: { calories: 210, protein: 35, carbs: 24, fat: 3, fibre: 1 } },
    { description: 'Salmon fillet, sweet potato, mixed greens', macros: { calories: 560, protein: 40, carbs: 42, fat: 18, fibre: 6 } },
  ],
  favourites: [
    { description: 'Grenade protein bar', macros: { calories: 220, protein: 20, carbs: 17, fat: 8, fibre: 2 } },
    { description: 'Chicken and avocado protein bagel', macros: { calories: 410, protein: 38, carbs: 35, fat: 12, fibre: 4 } },
  ],
  updatedAt: Date.now(),
};

// ── Workout Recents & Favourites ─────────────────────────────────

export const demoWorkoutRecentsFavourites = {
  userId: DEMO_USER_ID,
  recents: [
    { description: 'Upper body Push — bench, OHP, lateral raises, tricep dips', type: 'weightlifting', duration: 55 },
    { description: 'Evening walk', type: 'walk', duration: 35 },
  ],
  favourites: [],
  updatedAt: Date.now(),
};

// ── Aggregated accessor (single import point) ────────────────────

export const demoData = {
  // Static data
  profile: demoProfile,
  targets: demoTargets,
  bodyMetrics: demoBodyMetrics,
  tdeeConfig: demoTdeeConfig,
  deficitPlan: demoDeficitPlan,
  nutritionRecentsFavourites: demoNutritionRecentsFavourites,
  workoutRecentsFavourites: demoWorkoutRecentsFavourites,
  persona: demoPersona,

  // Dynamic data (functions — call to get today's data)
  nutrition: getDemoNutritionEntries,
  hydrationEntries: getDemoHydrationEntries,
  dailyNutrition: getDemoDailyNutrition,
  allDailyNutrition: getDemoAllDailyNutrition,
  dailyNutritionForDate: getDemoDailyNutritionForDate,
  supplements: getDemoSupplements,
  supplementEntries: getDemoSupplementEntries,
  workouts: getDemoWorkoutEntries,
  yesterdayWorkouts: getDemoYesterdayWorkoutEntries,
  allWorkouts: getDemoAllWorkouts,
  bodyStats: getDemoBodyStatsEntries,
  tdee: getDemoDailyTDEE,
  functionalAge: getDemoFunctionalAge,
  functionalAgeHistory: getDemoFunctionalAgeHistory,
  glp1StatusCard: getDemoGLP1StatusCard,
  glp1DoseDay: getDemoDoseDay,
} as const;